import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

// This store manages user profile information including personal details and transaction preferences.
// The profile data is persisted in sessionStorage to maintain user information across page refreshes.
// The store provides methods to update individual profile fields or clear the entire profile.

export enum TransactionTypes {
  BUY = "buy",
  SELL = "sell",
}

type UserProfileData = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  typeOfMiles: string;
  transactionType: TransactionTypes;
  productId: string;
  customerLeadId: string;
  customerId: string;
  initialMilesAmount: number;
};
type UserProfileUpdates = Partial<UserProfileData>;

interface UserProfileStore {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  typeOfMiles: string;
  transactionType: TransactionTypes;
  productId: string;
  customerLeadId: string;
  customerId: string;
  initialMilesAmount: number;
  setFirstName: (firstName: string) => void;
  setLastName: (lastName: string) => void;
  setEmail: (email: string) => void;
  setPhoneNumber: (phoneNumber: string) => void;
  setTypeOfMiles: (typeOfMiles: string) => void;
  setTransactionType: (transactionType: TransactionTypes) => void;
  setProductId: (productId: string) => void;
  setCustomerLeadId: (customerLeadId: string) => void;
  setCustomerId: (customerId: string) => void;
  setInitialMilesAmount: (initialMilesAmount: number) => void;
  updateProfile: (updates: UserProfileUpdates) => void;
  clearProfile: () => void;
  clearPartiallyProfile: () => void;
}

export const useUserCredentialsStore = create<UserProfileStore>()(
  persist(
    (set) => ({
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      typeOfMiles: "",
      transactionType: TransactionTypes.BUY,
      productId: "",
      customerLeadId: "",
      customerId: "",
      initialMilesAmount: 0,
      setFirstName: (firstName) => set(() => ({ firstName })),
      setLastName: (lastName) => set(() => ({ lastName })),
      setEmail: (email) => set(() => ({ email })),
      setPhoneNumber: (phoneNumber) => set(() => ({ phoneNumber })),
      setTypeOfMiles: (typeOfMiles) => set(() => ({ typeOfMiles })),
      setCustomerId: (customerId) => set(() => ({ customerId })),
      setTransactionType: (transactionType) => set(() => ({ transactionType })),
      setProductId: (productId) => set(() => ({ productId })),
      setCustomerLeadId: (customerLeadId) => set(() => ({ customerLeadId })),
      setInitialMilesAmount: (initialMilesAmount) =>
        set(() => ({ initialMilesAmount })),
      updateProfile: (updates) => set((state) => ({ ...state, ...updates })),
      clearProfile: () =>
        set(() => ({
          firstName: "",
          lastName: "",
          email: "",
          phoneNumber: "",
          typeOfMiles: "",
          transactionType: TransactionTypes.BUY,
          productId: "",
          customerLeadId: "",
          customerId: "",
          initialMilesAmount: 0,
        })),
      clearPartiallyProfile: () =>
        set(() => ({
          transactionType: TransactionTypes.BUY,
          productId: "",
          initialMilesAmount: 0,
          typeOfMiles: "",
        })),
    }),
    {
      name: "user-profile",
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
